/**
 * Application Constants and Enums
 * Centralized location for all constants used across the loan management system
 */

// Debt codes available in the system
export const DEBT_CODES = [
  'CC01', 'CC02', 'CC03', 'CC04', 'CC05', // Credit Card debts
  'PL01', 'PL02', 'PL03', 'PL04', 'PL05', // Personal Loans
  'AU01', 'AU02', 'AU03', 'AU04', 'AU05', // Auto Loans
  'MG01', 'MG02', 'MG03', 'MG04', 'MG05', // Mortgage debts
  'ST01', 'ST02', 'ST03', 'ST04', 'ST05'  // Student Loans
];

// Payment frequency options
export const PAYMENT_FREQUENCIES = {
  WEEKLY: 'weekly',
  BI_WEEKLY: 'bi-weekly',
  MONTHLY: 'monthly',
  QUARTERLY: 'quarterly',
  ANNUALLY: 'annually'
};

// Transaction types for audit trail
export const TRANSACTION_TYPES = {
  PAYMENT: 'payment',
  ADJUSTMENT: 'adjustment',
  FEE: 'fee',
  CONTACT: 'contact',
  ARRANGEMENT: 'arrangement',
  INTEREST: 'interest',
  LEGAL_FEE: 'legal_fee',
  DEBT_FEE: 'debt_fee'
};

// Contact types for communication tracking
export const CONTACT_TYPES = {
  PHONE_CALL: 'phone_call',
  EMAIL: 'email',
  LETTER: 'letter',
  IN_PERSON: 'in_person',
  SMS: 'sms'
};

// Navigation sections for the navigator panel
export const NAVIGATION_SECTIONS = {
  DEBT: 'debt',
  DEBTOR: 'debtor',
  DIALER: 'dialer',
  QUEUE: 'queue'
};

// Application states
export const APP_STATES = {
  LOADING: 'loading',
  READY: 'ready',
  ERROR: 'error',
  SAVING: 'saving'
};

// Form validation patterns
export const VALIDATION_PATTERNS = {
  PHONE: /^[\d\s\-\(\)\+\.]*$/,
  EMAIL: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  SSN: /^\d{3}-?\d{2}-?\d{4}$/,
  CURRENCY: /^\d+(\.\d{1,2})?$/
};

// Field length limits
export const FIELD_LIMITS = {
  CLIENT_NAME: 100,
  DEBT_CODE: 20,
  ADDRESS: 200,
  CONTACT: 100,
  COMMENT: 500,
  DESCRIPTION: 500,
  REFERENCE: 100,
  USER_ID: 50
};

// Currency formatting options
export const CURRENCY_OPTIONS = {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
};

// Date formatting options
export const DATE_OPTIONS = {
  SHORT: {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  },
  LONG: {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }
};

// Storage keys for localStorage
export const STORAGE_KEYS = {
  CLIENTS: 'loan_management_clients',
  DEBT_ACCOUNTS: 'loan_management_debt_accounts',
  PAYMENT_ARRANGEMENTS: 'loan_management_payment_arrangements',
  TRANSACTIONS: 'loan_management_transactions',
  APP_SETTINGS: 'loan_management_settings'
};

// Default values
export const DEFAULTS = {
  PAYMENT_FREQUENCY: PAYMENT_FREQUENCIES.MONTHLY,
  CURRENCY_PRECISION: 2,
  TRANSACTION_PAGE_SIZE: 50,
  SEARCH_DEBOUNCE_MS: 300
};

// Error messages
export const ERROR_MESSAGES = {
  REQUIRED_FIELD: 'This field is required',
  INVALID_EMAIL: 'Please enter a valid email address',
  INVALID_PHONE: 'Please enter a valid phone number',
  INVALID_SSN: 'Please enter a valid SSN (XXX-XX-XXXX)',
  INVALID_AMOUNT: 'Please enter a valid amount',
  NEGATIVE_AMOUNT: 'Amount cannot be negative',
  FUTURE_DATE_REQUIRED: 'Date must be in the future',
  INVALID_DEBT_CODE: 'Please select a valid debt code',
  SAVE_ERROR: 'Error saving data. Please try again.',
  LOAD_ERROR: 'Error loading data. Please refresh the page.',
  VALIDATION_ERROR: 'Please correct the errors below'
};

// Success messages
export const SUCCESS_MESSAGES = {
  CLIENT_SAVED: 'Client information saved successfully',
  PAYMENT_RECORDED: 'Payment recorded successfully',
  ARRANGEMENT_CREATED: 'Payment arrangement created successfully',
  ARRANGEMENT_SAVED: 'Payment arrangement saved successfully',
  DATA_EXPORTED: 'Data exported successfully',
  DATA_IMPORTED: 'Data imported successfully'
};